// EvaluacionRealtimePage.js — Fase H3 (rediseño)
// Modo Evaluación por Realtime API con UI pulida: glassmorphism, visualizer,
// dots de progreso por pregunta, chip de tipo, mobile-first, feedback como toast,
// pantalla final con score animado.

import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import styled, { keyframes, css } from 'styled-components';
import { useAuth } from '../contexts/AuthContext';
import { useProject } from '../contexts/ProjectContext';
import { useIdioma } from '../contexts/IdiomaContext';
import { evaluacionRealtimeService, mentor2Service, evaluacionEscritaService } from '../services/api';
import RealtimeService, { SESSION_STATES } from '../services/realtimeService';
import EvaluacionEscritaPage, { C as TemaClaro, temaEvaluacion, temaStreaming, PiezasStreaming, RaizS, EntradaS, FUENTE_S, PlayIco } from './EvaluacionEscritaPage';
import MuroCursos from '../components/landing/sections/MuroCursos';

// ═════ animations ═════
const slideUp     = keyframes`from { opacity:0; transform: translateY(20px); } to { opacity:1; transform: translateY(0); }`;
const slideRight  = keyframes`from { opacity:0; transform: translateX(40px); } to { opacity:1; transform: translateX(0); }`;
const fadeIn      = keyframes`from { opacity:0; } to { opacity:1; }`;
const pulse       = keyframes`0%,100% { transform: scale(1); opacity:.7; } 50% { transform: scale(1.18); opacity:1; }`;
const ringPulse   = keyframes`0% { box-shadow: 0 0 0 0 rgba(239,68,68,.55); } 100% { box-shadow: 0 0 0 22px rgba(239,68,68,0); }`;
const wave        = keyframes`0%,100% { transform: scaleY(0.4); } 50% { transform: scaleY(1.2); }`;
const countup     = keyframes`from { transform: scale(0.6); opacity:0; } 60% { transform: scale(1.08); opacity:1; } to { transform: scale(1); }`;
const confettiF   = keyframes`0% { transform: translateY(-50px) rotate(0); opacity:1; } 100% { transform: translateY(110vh) rotate(720deg); opacity:0; }`;
const slideDown   = keyframes`from { opacity:0; transform: translate(-50%, -20px); } to { opacity:1; transform: translate(-50%, 0); }`;

// ═════ layout ═════
const Page = styled.div`
  min-height: 100vh;
  background:
    radial-gradient(circle at 20% 10%, rgba(8,145,178,0.25) 0%, transparent 50%),
    radial-gradient(circle at 80% 90%, rgba(34,211,238,0.18) 0%, transparent 50%),
    linear-gradient(160deg, #07111d 0%, #0a1f3a 100%);
  color: #e2e8f0;
  display: flex; flex-direction: column;
  padding: clamp(0.75rem, 2vw, 1.5rem);
  /* Reservar el alto del header global (fijo/transparente en desktop) para que no tape la barra superior. */
  @media (min-width: 769px) { padding-top: 88px; }
  font-family: 'Inter', -apple-system, system-ui, sans-serif;
`;
const Container = styled.div`
  width: 100%; max-width: 760px; margin: 0 auto; flex: 1;
  display: flex; flex-direction: column; gap: 0.9rem;
`;
const TopBar = styled.div`
  display: flex; align-items: center; justify-content: space-between;
  padding: 0.25rem 0.1rem;
`;
const DocTitle = styled.div`
  font-size: 0.82rem; opacity: 0.7; font-weight: 600; letter-spacing: 0.3px;
  display: flex; align-items: center; gap: 0.5rem;
`;
const IdiomaPill = styled.span`
  background: rgba(255,255,255,0.08); border: 1px solid rgba(255,255,255,0.12);
  padding: 2px 9px; border-radius: 999px; font-size: 0.7rem; font-weight: 700;
  letter-spacing: 0.5px;
`;
const Timer = styled.div`
  font-size: 0.82rem; opacity: 0.7; font-variant-numeric: tabular-nums;
`;

// ─ Progress: dots per question ─
const Dots = styled.div`
  display: flex; gap: 6px; padding: 0.4rem 0;
  @media (max-width: 600px) { gap: 5px; }
`;
const Dot = styled.div`
  flex: 1; height: 6px; min-width: 0;
  border-radius: 999px;
  background: ${p => p.$s ? (p.$done ? 'var(--acento)' : p.$current ? 'rgba(255,255,255,.55)' : 'rgba(255,255,255,.14)') :
                     p.$claro ? (p.$done || p.$current ? 'var(--acento)' : '#cfe6e3') :
                     p.$done ? 'linear-gradient(90deg, #06b6d4, #22d3ee)' :
                     p.$current ? 'rgba(34,211,238,0.4)' :
                     'rgba(255,255,255,0.08)'};
  ${p => p.$current && css`animation: ${pulse} 1.6s ease infinite;`}
  transition: background 0.4s;
`;

// ─ Question Card ─
const QuestionCard = styled.div`
  background: linear-gradient(160deg, rgba(255,255,255,0.075) 0%, rgba(255,255,255,0.035) 100%);
  border: 1px solid rgba(255,255,255,0.1);
  border-radius: 22px;
  padding: clamp(1.5rem, 3.5vw, 2.25rem) clamp(1.25rem, 3.5vw, 2rem);
  backdrop-filter: blur(20px) saturate(1.3);
  -webkit-backdrop-filter: blur(20px) saturate(1.3);
  box-shadow:
    0 12px 40px rgba(0,0,0,0.35),
    inset 0 1px 0 rgba(255,255,255,0.08),
    inset 0 0 60px rgba(34,211,238,0.04);
  animation: ${slideUp} 0.5s cubic-bezier(0.16, 1, 0.3, 1);
  position: relative;
  overflow: hidden;
  &::before {
    content: '';
    position: absolute; inset: 0;
    background: radial-gradient(circle at 80% 0%, rgba(34,211,238,0.08) 0%, transparent 55%);
    pointer-events: none;
  }
`;
const WatermarkNum = styled.div`
  position: absolute;
  top: -20px; right: -10px;
  font-size: clamp(7rem, 18vw, 11rem);
  font-weight: 900; line-height: 0.85;
  color: rgba(34,211,238,0.05);
  font-family: 'Playfair Display', Georgia, serif;
  letter-spacing: -8px;
  pointer-events: none;
  user-select: none;
`;
const TipoChip = styled.span`
  position: relative; z-index: 1;
  display: inline-flex; align-items: center; gap: 6px;
  background: linear-gradient(135deg, rgba(34,211,238,0.18), rgba(6,182,212,0.1));
  color: #67e8f9;
  border: 1px solid rgba(34,211,238,0.35);
  padding: 5px 12px; border-radius: 999px;
  font-size: 0.7rem; font-weight: 700; letter-spacing: 1.2px; text-transform: uppercase;
  margin-bottom: 1.25rem;
  font-variant-numeric: tabular-nums;
`;
const PreguntaNum = styled.div`
  position: relative; z-index: 1;
  font-size: 0.72rem; opacity: 0.55; font-weight: 700; letter-spacing: 2px; text-transform: uppercase;
  margin-bottom: 0.6rem;
  display: flex; align-items: center; gap: 0.7rem;
  &::after {
    content: ''; flex: 0 1 60px; height: 1px;
    background: linear-gradient(90deg, rgba(34,211,238,0.4), transparent);
  }
`;
const Pregunta = styled.div`
  position: relative; z-index: 1;
  font-size: clamp(1.15rem, 3vw, 1.55rem);
  line-height: 1.55; color: #f8fafc; font-weight: 400;
  letter-spacing: 0.1px;
  min-height: 80px;
  animation: ${fadeIn} 0.4s ease;
  & strong { color: #22d3ee; font-weight: 600; }
`;
const Cursor = styled.span`
  display: inline-block; width: 2px; height: 1.1em; background: #22d3ee;
  margin-left: 4px; vertical-align: text-bottom;
  animation: ${pulse} 0.9s ease infinite;
`;

// ─ Audio visualizer ─
const Waves = styled.div`
  display: flex; align-items: center; justify-content: center;
  gap: 4px; height: 28px; margin-top: 0.75rem;
  opacity: ${p => p.$active ? 1 : 0.25}; transition: opacity 0.3s;
`;
const Bar = styled.div`
  width: 3px; height: 100%;
  background: linear-gradient(180deg, #22d3ee, #0891b2);
  border-radius: 2px; transform-origin: center;
  ${p => p.$active && css`animation: ${wave} ${0.6 + p.$i * 0.08}s ease infinite;`}
`;

// ─ Input zone ─
const InputCard = styled.div`
  background: rgba(255,255,255,0.04); border: 1px solid rgba(255,255,255,0.08);
  border-radius: 16px; padding: 1rem; backdrop-filter: blur(12px);
  display: flex; flex-direction: column; gap: 0.6rem;
`;
const Textarea = styled.textarea`
  width: 100%; box-sizing: border-box;
  background: rgba(0,0,0,0.25); color: #fff;
  border: 1px solid rgba(255,255,255,0.1); border-radius: 12px;
  padding: 0.85rem 1rem; font: inherit; font-size: 0.95rem; line-height: 1.4;
  resize: none; min-height: 56px; max-height: 160px;
  transition: border-color 0.2s;
  &:focus { outline: none; border-color: #22d3ee; box-shadow: 0 0 0 3px rgba(34,211,238,0.15); }
  &::placeholder { color: rgba(255,255,255,0.35); }
  &:disabled { opacity: 0.5; }
`;
const ActionsRow = styled.div`
  display: flex; gap: 0.5rem; align-items: center;
  @media (max-width: 480px) { flex-wrap: wrap; }
`;
const MicBtn = styled.button`
  position: relative;
  width: 56px; height: 56px; flex-shrink: 0;
  border-radius: 50%; border: none; cursor: pointer;
  background: ${p => p.$listening ? 'linear-gradient(135deg, #ef4444, #b91c1c)' :
                     p.$speaking ? 'linear-gradient(135deg, #06b6d4, #0e7490)' :
                     'rgba(255,255,255,0.08)'};
  color: #fff; font-size: 1.5rem;
  display: flex; align-items: center; justify-content: center;
  transition: transform 0.15s;
  ${p => p.$listening && css`animation: ${ringPulse} 1.4s ease infinite;`}
  &:hover { transform: scale(1.05); }
  &:disabled { opacity: 0.4; cursor: not-allowed; transform: none; animation: none; }
`;
const StatusLabel = styled.div`
  flex: 1; min-width: 0; font-size: 0.85rem; opacity: 0.7;
  display: flex; align-items: center; gap: 0.4rem;
`;
const StatusDot = styled.span`
  width: 8px; height: 8px; border-radius: 50%;
  background: ${p => p.$color || '#94a3b8'};
  box-shadow: 0 0 8px ${p => p.$color || '#94a3b8'};
`;
const SendBtn = styled.button`
  background: linear-gradient(135deg, #0891b2, #0e7490);
  color: #fff; border: none; border-radius: 12px;
  padding: 0.75rem 1.25rem; font-weight: 700; font-size: 0.9rem; cursor: pointer;
  transition: transform 0.15s, opacity 0.15s;
  &:hover { transform: translateY(-1px); }
  &:disabled { opacity: 0.4; cursor: not-allowed; transform: none; }
`;
const QuickChips = styled.div`
  display: flex; flex-wrap: wrap; gap: 0.4rem; margin-top: 0.6rem;
`;
const QuickChip = styled.button`
  background: rgba(34,211,238,0.1); color: #22d3ee;
  border: 1px solid rgba(34,211,238,0.3);
  border-radius: 999px; padding: 6px 14px; font-size: 0.82rem; font-weight: 600;
  cursor: pointer; transition: all 0.15s;
  &:hover { background: rgba(34,211,238,0.25); transform: translateY(-1px); }
`;

// ─ Feedback toast ─
const FeedbackToast = styled.div`
  position: fixed; top: 70px; left: 50%; transform: translateX(-50%);
  background: rgba(15,23,42,0.97); color: #fff;
  border-left: 4px solid ${p => p.$color || '#94a3b8'};
  border-radius: 12px; padding: 0.9rem 1.1rem; min-width: 260px; max-width: 92vw;
  box-shadow: 0 12px 40px rgba(0,0,0,0.55);
  z-index: 50; animation: ${slideDown} 0.35s ease;
  backdrop-filter: blur(14px);
`;
const FeedbackBadge = styled.div`
  display: inline-block; font-size: 0.7rem; font-weight: 800; letter-spacing: 0.8px;
  color: ${p => p.$color}; text-transform: uppercase; margin-bottom: 4px;
`;

// ─ Final screen ─
const ResultCard = styled.div`
  background: rgba(255,255,255,0.06); border: 1px solid rgba(255,255,255,0.1);
  border-radius: 24px; padding: 2.5rem 1.5rem; text-align: center;
  backdrop-filter: blur(20px); box-shadow: 0 20px 60px rgba(0,0,0,0.4);
  animation: ${slideUp} 0.5s ease;
  position: relative; overflow: hidden;
`;
const Score = styled.div`
  font-size: clamp(4rem, 14vw, 6.5rem); font-weight: 900;
  background: ${p => p.$ok ? 'linear-gradient(135deg, #34d399, #10b981, #059669)' :
                     'linear-gradient(135deg, #fca5a5, #ef4444, #dc2626)'};
  -webkit-background-clip: text; background-clip: text; color: transparent;
  line-height: 1; margin: 0.5rem 0; letter-spacing: -2px;
  animation: ${countup} 0.7s cubic-bezier(0.34, 1.56, 0.64, 1);
`;
const ResultBadge = styled.div`
  display: inline-flex; align-items: center; gap: 6px;
  padding: 0.5rem 1.1rem; border-radius: 999px;
  font-size: 0.85rem; font-weight: 800; letter-spacing: 0.5px;
  background: ${p => p.$ok ? 'rgba(16,185,129,0.18)' : 'rgba(239,68,68,0.18)'};
  color: ${p => p.$ok ? '#34d399' : '#fca5a5'};
  border: 1px solid ${p => p.$ok ? 'rgba(16,185,129,0.45)' : 'rgba(239,68,68,0.45)'};
`;
const CertBlock = styled.div`
  margin-top: 1.25rem; padding: 1rem 1.25rem;
  background: linear-gradient(135deg, rgba(16,185,129,0.15), rgba(6,182,212,0.1));
  border: 1px solid rgba(16,185,129,0.3); border-radius: 14px;
  display: flex; align-items: center; gap: 0.75rem; justify-content: center;
`;
const Confetto = styled.div`
  position: fixed; top: -10px; width: 8px; height: 14px;
  background: ${p => p.$c};
  left: ${p => p.$x}%;
  animation: ${confettiF} ${p => 2.5 + p.$d}s ease-out forwards;
  animation-delay: ${p => p.$delay}s;
  pointer-events: none; z-index: 100;
`;

// ─ Generic buttons ─
const Btn = styled.button`
  background: ${p => p.$primary ? 'linear-gradient(135deg, #0891b2, #0e7490)' : 'rgba(255,255,255,0.08)'};
  color: #fff; border: 1px solid ${p => p.$primary ? 'transparent' : 'rgba(255,255,255,0.15)'};
  border-radius: 12px; padding: 0.75rem 1.25rem; font-weight: 700; font-size: 0.9rem;
  cursor: pointer; transition: transform 0.15s;
  &:hover { transform: translateY(-1px); }
  &:disabled { opacity: 0.45; cursor: not-allowed; transform: none; }
`;
const Hint = styled.div`
  font-size: 0.78rem; opacity: 0.5; text-align: center; margin-top: 0.5rem;
`;

// ═════ Diseño «claro» (ficha → evaluacion_tema.diseno = 'claro', p. ej. INS) ═════
// Las mismas piezas de la evaluación oral, repintadas con los colores del proyecto
// (variables --acento, --fondo, --texto, --error que pone temaEvaluacion()).
const PageClaro = styled(Page)`
  --acento: ${p => p.$acento || '#32b5a9'}; --fondo: ${p => p.$fondo || '#ffffff'}; --tarjeta: ${p => p.$tarjeta || '#e8f7f6'};
  --texto: ${p => p.$texto || '#3f3f3e'}; --error: ${p => p.$error || '#e1312a'};
  background: var(--fondo); color: var(--texto);
  font-family: ${p => p.$fuente || "'Nunito', Helvetica, Arial, sans-serif"};
  @media (min-width: 769px) { padding-top: clamp(1rem, 3vw, 2.5rem); }
  ${DocTitle} { opacity: 1; color: var(--acento); font-weight: 800; }
  ${IdiomaPill} { background: #fff; color: var(--acento); border-color: var(--acento); }
  ${Timer} { opacity: 1; color: #6b7a7b; }
  ${QuestionCard}, ${ResultCard} {
    background: var(--tarjeta); border: 1px solid #d3ebe8; border-radius: 16px; color: var(--texto);
    box-shadow: 0 10px 30px rgba(20, 90, 82, .08); backdrop-filter: none; -webkit-backdrop-filter: none;
    &::before { display: none; }
  }
  ${WatermarkNum} { color: rgba(50, 181, 169, .08); }
  ${TipoChip} { background: #fff; color: var(--acento); border-color: var(--acento); font-weight: 800; }
  ${PreguntaNum} { opacity: 1; color: #7a8a8c; font-weight: 800;
    &::after { background: linear-gradient(90deg, var(--acento), transparent); } }
  ${Pregunta} { color: #1f2d2c; font-weight: 700; & strong { color: var(--acento); } }
  ${Cursor} { background: var(--acento); }
  ${Bar} { background: var(--acento); }
  ${InputCard} { background: var(--tarjeta); border-color: #d3ebe8; backdrop-filter: none; }
  ${Textarea} { background: #fff; color: var(--texto); border-color: #cfe3e1;
    &:focus { border-color: var(--acento); box-shadow: 0 0 0 3px rgba(50, 181, 169, .18); }
    &::placeholder { color: #9aa8a9; } }
  ${MicBtn} { color: #fff; }
  ${StatusLabel} { opacity: 1; color: #5b6a6b; }
  ${SendBtn} { background: var(--acento); }
  ${QuickChip} { background: #fff; color: var(--acento); border-color: var(--acento);
    &:hover { background: #d6efec; } }
  ${FeedbackToast} { background: #fff; color: var(--texto); box-shadow: 0 12px 34px rgba(20, 90, 82, .18); }
  ${Score} { background: none; -webkit-background-clip: border-box; background-clip: border-box; }
  ${ResultBadge} { font-weight: 800; }
  ${CertBlock} { background: #fff; border-color: var(--acento); }
  ${Btn} { background: #fff; color: var(--acento); border: 1px solid var(--acento); text-transform: uppercase; letter-spacing: .3px; font-weight: 800; }
  ${Hint} { opacity: 1; color: #7a8a8c; }
  h1 { color: var(--acento); font-weight: 800; text-transform: uppercase; }
  details { background: var(--tarjeta) !important; border: 1px solid #d3ebe8; }
  details summary { color: var(--acento); font-weight: 800; opacity: 1 !important; }
`;
// Botón principal del diseño claro (relleno con el color del proyecto).
const BtnClaro = styled(Btn)`
  && { background: var(--acento); color: #fff; border-color: var(--acento); }
`;

// ═════ Diseño «streaming» (Learning: portada catalogo_streaming) ═════
// Negro y el acento del catálogo (rojo), Bricolage y los botones de la portada: igual que la escrita.
const PageS = styled(Page)`
  --acento: ${p => p.$acento || '#E50914'}; --fondo: ${p => p.$fondo || '#141414'};
  background: var(--fondo); color: #d5d9df; font-family: ${FUENTE_S};
  padding: clamp(5.5rem, 9vw, 7.5rem) 16px clamp(2.5rem, 5vw, 4rem);
  @media (max-width: 760px) { padding-top: 5rem; }
  ${Container} { max-width: 820px; }
  ${DocTitle} { opacity: 1; font-size: .95rem; color: #e7eaee;
    &::before { content: ''; flex: none; width: 28px; height: 2px; background: var(--acento); } }
  ${IdiomaPill} { border-radius: 4px; background: rgba(255,255,255,.1); border: 0; color: #cfd3d8; }
  ${Timer} { opacity: 1; color: #9aa1ab; }
  ${QuestionCard}, ${ResultCard} {
    background: #1d1d1d; border: 0; border-radius: 8px; box-shadow: inset 0 0 0 1px rgba(255,255,255,.06);
    backdrop-filter: none; -webkit-backdrop-filter: none;
    &::before { display: none; }
  }
  ${WatermarkNum} { color: rgba(255,255,255,.04); font-family: ${FUENTE_S}; font-stretch: 76%; letter-spacing: -4px; }
  ${TipoChip} { background: none; border: 0; padding: 0; color: #9aa1ab; text-transform: none; letter-spacing: 0; font-size: .85rem; font-weight: 600; }
  ${PreguntaNum} { opacity: 1; color: #9aa1ab; text-transform: none; letter-spacing: 0; font-size: .85rem; font-weight: 600;
    &::after { background: linear-gradient(90deg, var(--acento), transparent); } }
  ${Pregunta} { color: #fff; font-weight: 700; font-stretch: 92%; line-height: 1.35; & strong { color: #fff; text-decoration: underline; text-decoration-color: var(--acento); } }
  ${Cursor} { background: var(--acento); }
  ${Bar} { background: var(--acento); }
  ${InputCard} { background: #1d1d1d; border: 0; border-radius: 8px; box-shadow: inset 0 0 0 1px rgba(255,255,255,.06); backdrop-filter: none; }
  ${Textarea} { background: #141414; border-radius: 6px; border-color: rgba(255,255,255,.16);
    &:focus { border-color: #fff; box-shadow: none; }
    &::placeholder { color: #6f7680; } }
  ${StatusLabel} { opacity: 1; color: #9aa1ab; }
  ${SendBtn} { background: #fff; color: #111; border-radius: 8px; &:hover:not(:disabled) { background: rgba(255,255,255,.86); } }
  ${QuickChip} { background: #262626; color: #fff; border: 0; box-shadow: inset 0 0 0 1px rgba(255,255,255,.1);
    &:hover { background: #303030; } }
  ${FeedbackToast} { background: #1d1d1d; box-shadow: 0 12px 34px rgba(0,0,0,.6), inset 0 0 0 1px rgba(255,255,255,.08); }
  ${Score} { background: none; -webkit-background-clip: border-box; background-clip: border-box; font-stretch: 76%; }
  ${CertBlock} { background: rgba(61,220,132,.1); border-color: rgba(61,220,132,.35); color: #c9f5dc; }
  ${Btn} { background: rgba(109,109,110,.6); border: 0; border-radius: 8px; height: 48px; padding: 0 1.4rem; font-size: 1rem;
    &:hover:not(:disabled) { background: rgba(109,109,110,.4); transform: none; } }
  ${Hint} { opacity: 1; color: #7d848e; }
  h1, h2 { color: #fff; font-weight: 800; font-stretch: 85%; }
  details { background: #1d1d1d !important; border-radius: 8px !important; }
`;
// Botón principal en el diseño streaming: blanco, como «Ver curso» en la portada.
const BtnS = styled(Btn)`
  && { background: #fff; color: #111; border: 0; border-radius: 8px; height: 48px; padding: 0 1.4rem; font-size: 1rem; display: inline-flex; align-items: center; gap: .5rem; }
  && svg { width: 18px; height: 18px; }
  &&:hover:not(:disabled) { background: rgba(255,255,255,.86); transform: none; }
`;
// Pantalla de elección en el diseño streaming: dos opciones grandes, con su ícono.
const OpcionesModo = styled.div`
  display: grid; grid-template-columns: repeat(2, minmax(0, 1fr)); gap: .8rem; max-width: 46rem; margin-top: 1.8rem;
  @media (max-width: 640px) { grid-template-columns: 1fr; }
  button {
    display: grid; grid-template-columns: auto 1fr; align-items: center; gap: .3rem 1rem; padding: 1.1rem 1.25rem; border: 0; border-radius: 8px;
    background: rgba(0,0,0,.5); box-shadow: inset 0 0 0 1px rgba(255,255,255,.14); color: #fff; text-align: left; cursor: pointer;
    font: inherit; backdrop-filter: blur(8px); -webkit-backdrop-filter: blur(8px); transition: background .15s, box-shadow .15s;
  }
  button:hover, button:focus-visible { background: rgba(0,0,0,.66); box-shadow: inset 0 0 0 2px var(--acento); outline: none; }
  i { grid-row: span 2; display: grid; place-items: center; width: 48px; height: 48px; border-radius: 50%; background: var(--acento); color: #fff; }
  i svg { width: 22px; height: 22px; }
  b { font-size: 1.2rem; font-weight: 800; }
  span { font-size: .9rem; color: #c3c9d1; line-height: 1.4; }
`;
const IcoMic = () => <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" aria-hidden="true"><rect x="9" y="3" width="6" height="11" rx="3" /><path d="M5.5 11a6.5 6.5 0 0 0 13 0M12 17.5V21" /></svg>;
const IcoLapiz = () => <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinejoin="round" aria-hidden="true"><path d="M4 20h4L19 9l-4-4L4 16z" /><path d="m13.5 6.5 4 4" strokeLinecap="round" /></svg>;

const TIPO_LABEL = {
  vf: '✓ / ✗  Verdadero o Falso',
  seleccion: '◉  Selección múltiple',
  abierta: '✎  Respuesta abierta',
  completar: '_  Completar',
  lista: '☰  Enumeración',
};

// Detecta el tipo de la pregunta ACTUAL desde su texto (multi-idioma).
// Más robusto que usar el tipo de la pregunta anterior (que es lo que enviaba submit).
const detectarTipoDeTexto = (texto) => {
  if (!texto || texto.length < 8) return null;
  const t = texto.toLowerCase();
  // Verdadero/Falso
  if (/(verdadero o falso|verdadero\/falso|true or false|true\/false|verdadeiro ou falso|verdadeiro\/falso|¿verdadero|is it true|é verdadeiro)/i.test(t)) return 'vf';
  // Selección múltiple: A: / B: / A) / A. / "opción A" / "cuál de las siguientes"
  if (/(\b[ABCDabcd]\s*[\)\.\:\-]\s)|(\bopci[oó]n\s+[abcd]\b)|(\boption\s+[abcd]\b)|(\bop[cç][aã]o\s+[abcd]\b)|(cu[aá]l de las siguientes|cu[aá]l de las opciones|which of the following|qual das (seguintes|op[cç][oõ]es))/i.test(texto)) return 'seleccion';
  // Completar: blanco/línea/puntos
  if (/_{3,}|\.{3,}|\[\.\.\.\]|complete? the|completa la|complete? a frase|llena el espacio|fill in the blank/i.test(t)) return 'completar';
  // Enumeración / lista
  if (/(enumera|menciona (los|al menos|tres|dos)|name (some|the|three|two)|cite (alguns|dois|tr[eê]s))/i.test(t)) return 'lista';
  return 'abierta';
};
const NIVEL_COLOR = { completa: '#10b981', parcial: '#06b6d4', minima: '#f59e0b', incorrecta: '#ef4444' };
const NIVEL_EMOJI = { completa: '✅', parcial: '👍', minima: '🤔', incorrecta: '❌' };

// ─ ErrorBoundary ─
class EvalErrorBoundary extends React.Component {
  constructor(props) { super(props); this.state = { error: null }; }
  static getDerivedStateFromError(error) { return { error }; }
  componentDidCatch(error, info) { console.error('[EvalBoundary]', error, info); }
  render() {
    if (this.state.error) {
      return (
        <Page>
          <Container>
            <QuestionCard>
              <h2 style={{ color: '#fca5a5', marginTop: 0 }}>⚠️ Error en la evaluación</h2>
              <pre style={{ background: 'rgba(0,0,0,0.4)', padding: '0.8rem', borderRadius: 8, overflow: 'auto', fontSize: '0.78rem', color: '#fca5a5', maxHeight: 220 }}>
                {String(this.state.error?.message || this.state.error)}
                {this.state.error?.stack ? '\n\n' + this.state.error.stack.split('\n').slice(0, 6).join('\n') : ''}
              </pre>
              <div style={{ display: 'flex', gap: '0.6rem', marginTop: '1rem' }}>
                <Btn onClick={() => window.location.reload()}>Recargar</Btn>
                <Btn onClick={() => window.history.back()}>Volver</Btn>
              </div>
            </QuestionCard>
          </Container>
        </Page>
      );
    }
    return this.props.children;
  }
}

const fmtMMSS = (ms) => {
  const s = Math.max(0, Math.round(ms / 1000));
  return `${String(Math.floor(s / 60)).padStart(2, '0')}:${String(s % 60).padStart(2, '0')}`;
};

// ═════════ MAIN PAGE ═════════
const EvaluacionRealtimePage = () => {
  const { documentId } = useParams();
  const [paramsBusqueda] = useSearchParams();
  const examenId = parseInt(paramsBusqueda.get('examen') || '', 10) || null;   // examen de módulo/lección/curso
  const navigate = useNavigate();
  const { user } = useAuth();
  const { proyecto } = useProject();
  const { t, idioma } = useIdioma();
  // Diseño claro del proyecto (ficha → Diseño de la evaluación), igual que la escrita.
  const temaS = temaStreaming(proyecto);            // Learning: negro y rojo, como la portada
  const temaClaro = temaS ? null : temaEvaluacion(proyecto);
  const Pg = temaS ? PageS : temaClaro ? PageClaro : Page;
  const BtnP = temaS ? BtnS : temaClaro ? BtnClaro : Btn;          // botón principal
  const sobrio = !!(temaS || temaClaro);            // sin emojis ni confeti
  const temaProps = temaS || temaClaro || {};
  const suave = temaClaro ? '#6b7a7b' : 'rgba(255,255,255,0.7)';
  const colorNivel = (n) => (temaClaro && n === 'completa' ? 'var(--acento)' : temaClaro && n === 'incorrecta' ? 'var(--error)' : (NIVEL_COLOR[n] || '#94a3b8'));

  const [stage, setStage] = useState('pre'); // pre | conectando | en_curso | finalizando | resultado | error
  // Datos de la portada de entrada (diseño streaming): título, preguntas, % e intentos; no crea intento.
  const [infoS, setInfoS] = useState(null);
  const conTemaS = !!temaS;
  useEffect(() => {
    if (!conTemaS) return;
    evaluacionEscritaService.info(parseInt(documentId, 10), examenId).then(r => { if (r.data?.success) setInfoS(r.data); }).catch(() => {});
  }, [conTemaS, documentId, examenId]);
  const [meta, setMeta] = useState(null);
  const [progress, setProgress] = useState(0);
  const [chatLog, setChatLog] = useState([]);
  const [streamingAi, setStreamingAi] = useState('');
  const [inputText, setInputText] = useState('');
  const [feedbackToast, setFeedbackToast] = useState(null);
  const [resultado, setResultado] = useState(null);
  const [error, setError] = useState(null);
  const [aiState, setAiState] = useState(SESSION_STATES?.DISCONNECTED || 'disconnected');
  const [tipoActual, setTipoActual] = useState(null); // tipo de la última pregunta evaluada
  const [tickMs, setTickMs] = useState(0); // refresh para el cronómetro
  const [thinking, setThinking] = useState(false); // submit en curso (UX: "Procesando…")
  const [micUnavailable, setMicUnavailable] = useState(false);
  const [noVoiceDetected, setNoVoiceDetected] = useState(false);

  const clientRef = useRef(null);
  const startTimeRef = useRef(null);
  const respuestasRef = useRef([]);
  const streamingBufferRef = useRef('');
  const streamingTickRef = useRef(null);
  const feedbackTimerRef = useRef(null);
  // Acumulamos lo que el alumno DIJO o ESCRIBIÓ por pregunta. Se reinicia con
  // cada submit_pregunta_evaluacion (cada pregunta es independiente).
  const userInputsCurrentRef = useRef([]);

  const totalPreguntas = meta?.total_preguntas || 0;
  const porcAprob = meta?.porcentaje_aprobacion || 60;

  // ── cronómetro
  useEffect(() => {
    if (stage !== 'en_curso') return;
    const t = setInterval(() => setTickMs(Date.now() - (startTimeRef.current || Date.now())), 1000);
    return () => clearInterval(t);
  }, [stage]);

  const appendLog = useCallback((role, text) => {
    if (!text) return;
    setChatLog(prev => [...prev, { role, text, ts: Date.now() }]);
  }, []);

  const scheduleStreamingFlush = useCallback(() => {
    if (streamingTickRef.current) return;
    streamingTickRef.current = requestAnimationFrame(() => {
      streamingTickRef.current = null;
      setStreamingAi(streamingBufferRef.current);
    });
  }, []);

  const showFeedback = useCallback((data) => {
    setFeedbackToast(data);
    if (feedbackTimerRef.current) clearTimeout(feedbackTimerRef.current);
    feedbackTimerRef.current = setTimeout(() => setFeedbackToast(null), 5000);
  }, []);

  const finalizar = useCallback(async (args) => {
    setStage('finalizando');
    const duracionMin = Math.max(1, Math.round((Date.now() - (startTimeRef.current || Date.now())) / 60000));
    const correctas = respuestasRef.current.filter(r => r.nivel === 'completa' || r.nivel === 'parcial').length;
    try {
      const res = await evaluacionRealtimeService.guardar({
        document_id: parseInt(documentId, 10),
        proyecto_id: proyecto?.id || null,
        total_preguntas: totalPreguntas || respuestasRef.current.length,
        preguntas_correctas: correctas,
        porcentaje: args.porcentaje,
        aprobado: !!args.aprobado,
        examen_id: examenId,
        detalle_respuestas: respuestasRef.current,
        duracion_minutos: duracionMin,
        idioma,
        resumen_global: args.resumen_global || '',
      });
      // Con examen, «aprobado» lo decide el servidor con la nota del examen.
      setResultado({ ...res.data, porcentaje: args.porcentaje, aprobado: examenId ? !!res.data?.aprobado : !!args.aprobado, resumen_global: args.resumen_global || '' });
      setStage('resultado');
    } catch (e) {
      setError(e?.response?.data?.message || e?.message || 'Error guardando');
      setStage('error');
    } finally {
      clientRef.current?.disconnect();
    }
  }, [documentId, idioma, proyecto?.id, totalPreguntas, examenId]);

  const handleFunctionCall = useCallback(async (name, args) => {
    if (name === 'submit_pregunta_evaluacion') {
      const userTotalChars = userInputsCurrentRef.current.join(' ').trim().length;
      const respuesta = String(args.respuesta_usuario || '').trim();

      // Defensa: el alumno NUNCA habló ni escribió para esta pregunta → el modelo
      // inventó la respuesta. Rechazar.
      if (userTotalChars === 0) {
        setNoVoiceDetected(true);
        return {
          output: JSON.stringify({
            error: 'no_voice_detected',
            message: 'No se capturó respuesta del alumno para esta pregunta. PROHIBIDO inventar texto. Decile cortésmente: "No escuché tu respuesta, ¿podés repetirla o escribirla?" y espera.',
          }),
          triggerResponse: true,
        };
      }
      if (respuesta.length < 2) {
        return {
          output: JSON.stringify({
            error: 'no_response_yet',
            message: 'Respuesta vacía. Esperá una respuesta clara antes de llamar submit.',
          }),
          triggerResponse: false,
        };
      }

      respuestasRef.current = [...respuestasRef.current, args];
      setProgress(p => p + 1);
      setTipoActual(args.tipo_pregunta || null);
      setNoVoiceDetected(false);
      userInputsCurrentRef.current = []; // reset para la siguiente pregunta
      showFeedback({
        nivel: args.nivel, puntaje: args.puntaje, feedback: args.feedback,
        color: NIVEL_COLOR[args.nivel] || '#94a3b8',
      });
      // triggerResponse:true → tras el ack, el modelo encadena la siguiente pregunta.
      // El submit es silencioso (no genera audio porque el response solo contiene el
      // function_call). El siguiente response empieza directo con "Pregunta K de N: ...".
      return { output: 'recorded', triggerResponse: true };
    }
    if (name === 'finalizar_evaluacion') {
      await finalizar(args);
      // Sesión termina; no necesitamos que el modelo siga generando.
      return { output: 'finalized', triggerResponse: false };
    }
    return null;
  }, [finalizar, showFeedback]);

  const startSession = useCallback(async () => {
    setError(null);
    setStage('conectando');
    respuestasRef.current = [];
    setProgress(0); setChatLog([]); setStreamingAi('');
    startTimeRef.current = Date.now();

    const client = new RealtimeService({
      documentId: parseInt(documentId, 10),
      mode: 'evaluacion',
      sessionEndpoint: '/evaluacion-realtime/iniciar.php',
      extraSessionPayload: examenId ? { examen_id: examenId } : null,
      autoGreeting: false,
      onSessionReady: (data) => {
        setMeta({
          total_preguntas: data.total_preguntas,
          porcentaje_aprobacion: data.porcentaje_aprobacion,
          document_title: data.document_title,
          voice: data.voice, model: data.model,
        });
      },
      onAITranscript: (text, isDone) => {
        if (!isDone) {
          streamingBufferRef.current = (streamingBufferRef.current || '') + (text || '');
          scheduleStreamingFlush();
        } else {
          if (streamingTickRef.current) { cancelAnimationFrame(streamingTickRef.current); streamingTickRef.current = null; }
          const full = text || streamingBufferRef.current;
          if (full && full.trim()) appendLog('ai', full.trim());
          streamingBufferRef.current = ''; setStreamingAi('');
        }
      },
      onTranscript: (text, isDone) => {
        if (isDone !== false && text && text.trim()) {
          appendLog('user', text.trim());
          userInputsCurrentRef.current.push(text.trim());
          setNoVoiceDetected(false);
        }
      },
      onStateChange: (s) => setAiState(s),
      onFunctionCall: handleFunctionCall,
      onThinking: (on) => setThinking(!!on),
      onMicError: () => { setMicUnavailable(true); },
      onError: (e) => { setError(e || 'Error de conexión'); setStage('error'); },
    });
    clientRef.current = client;
    setStage('en_curso');
    await client.connect();
  }, [appendLog, documentId, handleFunctionCall, scheduleStreamingFlush, examenId]);

  const sendText = () => {
    const txt = inputText.trim();
    if (!txt) return;
    appendLog('user', txt);
    userInputsCurrentRef.current.push(txt);
    setNoVoiceDetected(false);
    clientRef.current?.sendUserText(txt);
    setInputText('');
  };

  const sendQuick = (txt) => {
    appendLog('user', txt);
    userInputsCurrentRef.current.push(txt);
    setNoVoiceDetected(false);
    clientRef.current?.sendUserText(txt);
  };

  useEffect(() => () => { clientRef.current?.disconnect(); }, []);

  // ── render utilities ──
  const ultimaAi = [...chatLog].reverse().find(l => l.role === 'ai')?.text || '';
  const currentQuestion = streamingAi || ultimaAi || '';

  const statusInfo = () => {
    if (stage === 'conectando') return { dot: '#f59e0b', txt: 'Conectando con el evaluador…' };
    if (stage === 'finalizando') return { dot: '#06b6d4', txt: 'Guardando tu resultado…' };
    if (thinking) {
      const txt = idioma === 'en' ? 'Recording your answer…' : idioma === 'pt' ? 'Registrando sua resposta…' : 'Registrando tu respuesta…';
      return { dot: '#fbbf24', txt };
    }
    if (aiState === SESSION_STATES.AI_SPEAKING) return { dot: '#22d3ee', txt: 'El evaluador está hablando' };
    if (aiState === SESSION_STATES.LISTENING) return { dot: '#ef4444', txt: 'Te estoy escuchando' };
    return { dot: '#10b981', txt: 'Tu turno — habla o escribe' };
  };

  // Tipo de la pregunta ACTUAL (detectada del texto que el modelo está diciendo).
  // Más fiable que tipoActual (que era el de la pregunta anterior recién evaluada).
  const tipoCurrent = detectarTipoDeTexto(currentQuestion);

  // Quick chips según el tipo detectado de la pregunta que el modelo acaba de hacer.
  const quickChips = () => {
    if (tipoCurrent === 'vf') return idioma === 'en' ? ['True', 'False'] : idioma === 'pt' ? ['Verdadeiro', 'Falso'] : ['Verdadero', 'Falso'];
    if (tipoCurrent === 'seleccion') return ['A', 'B', 'C', 'D'];
    return null;
  };

  // ═════ PRE ═════
  if (stage === 'pre' && temaS) {
    const quedan = infoS?.max_intentos > 0 ? Math.max(0, infoS.max_intentos - (infoS.intentos_usados || 0)) : null;
    return (
      <RaizS {...temaS}>
        <EntradaS>
          <MuroCursos proyecto={proyecto} fondo={temaS.$fondo || '#141414'} acento={temaS.$acento || '#E50914'} opacidad={0.6} />
          <span className="eyebrow">Evaluación oral</span>
          <h1>{infoS?.titulo || (infoS?.curso ? `Evaluación de ${infoS.curso}` : 'Evaluación')}</h1>
          <p className="bajada">
            {infoS?.titulo && infoS?.curso && <>Del curso <b>{infoS.curso}</b>. </>}
            El evaluador te hará las preguntas en voz alta y tú respondes hablando o escribiendo. Permite el micrófono cuando el navegador lo pida.
          </p>
          {infoS && (
            <dl>
              <div><dd>{infoS.total_preguntas}</dd><dt>preguntas</dt></div>
              <div><dd>{Math.round(infoS.porcentaje_aprobacion)} %</dd><dt>para aprobar</dt></div>
              <div>
                <dd>{quedan === null ? 'Libres' : quedan}</dd>
                <dt>{quedan === null ? 'intentos, sin límite' : `${quedan === 1 ? 'intento disponible' : 'intentos disponibles'} de ${infoS.max_intentos}`}</dt>
              </div>
            </dl>
          )}
          {quedan === 0 && <p className="aviso">Ya usaste todos tus intentos de esta evaluación.</p>}
          <div className="botones">
            <PiezasStreaming.Btn $primary onClick={startSession} disabled={quedan === 0}><PlayIco /> Comenzar evaluación</PiezasStreaming.Btn>
            <PiezasStreaming.Btn onClick={() => navigate(examenId ? `/mentor/${documentId}` : '/contenidos')}>Volver al curso</PiezasStreaming.Btn>
          </div>
        </EntradaS>
      </RaizS>
    );
  }
  if (stage === 'pre') {
    return (
      <Pg {...temaProps}>
        <Container>
          <QuestionCard style={{ textAlign: 'center', paddingTop: '3rem', paddingBottom: '2.5rem' }}>
            {!sobrio && <div style={{ fontSize: '3.5rem', marginBottom: '0.5rem' }}>📝</div>}
            <h1 style={{ margin: '0 0 0.75rem', fontSize: 'clamp(1.5rem, 4vw, 2rem)' }}>Evaluación</h1>
            <p style={{ color: suave, margin: '0 auto 2rem', maxWidth: 460, lineHeight: 1.6 }}>
              Tu evaluador IA hará varias preguntas en <strong>{(idioma || 'es').toUpperCase()}</strong>.
              Puedes <strong>hablar</strong> o <strong>escribir</strong> tus respuestas. Necesitas aprobar para obtener el certificado.
            </p>
            <BtnP $primary onClick={startSession} style={{ padding: '1rem 2rem', fontSize: '1rem' }}>
              {sobrio ? 'Comenzar evaluación' : '🎙️ Comenzar evaluación'}
            </BtnP>
            <Hint style={{ marginTop: '1.25rem' }}>Asegúrate de permitir el micrófono cuando lo pida el navegador</Hint>
          </QuestionCard>
        </Container>
      </Pg>
    );
  }

  // ═════ ERROR ═════
  if (stage === 'error') {
    return (
      <Pg {...temaProps}>
        <Container>
          <QuestionCard style={{ textAlign: 'center' }}>
            <h2 style={{ color: temaClaro ? 'var(--error)' : '#fca5a5' }}>{sobrio ? 'Algo salió mal' : '⚠️ Algo salió mal'}</h2>
            <p style={{ color: suave }}>{error}</p>
            <Btn onClick={() => navigate(-1)}>Volver</Btn>
          </QuestionCard>
        </Container>
      </Pg>
    );
  }

  // ═════ RESULTADO ═════
  if (stage === 'resultado' && resultado) {
    const ok = resultado.aprobado;
    return (
      <Pg {...temaProps}>
        {/* Confetti si pasa */}
        {ok && !sobrio && Array.from({ length: 30 }).map((_, i) => (
          <Confetto key={i}
            $x={Math.random() * 100}
            $c={['#22d3ee', '#10b981', '#06b6d4', '#34d399', '#fbbf24'][i % 5]}
            $d={Math.random() * 1.5}
            $delay={Math.random() * 0.6}
          />
        ))}
        <Container>
          <ResultCard>
            {sobrio
              ? <div aria-hidden="true" style={{ width: 64, height: 64, margin: '0 auto .4rem', borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '2rem', fontWeight: 900, color: '#fff', background: temaS ? (ok ? '#1f9d55' : 'var(--acento)') : (ok ? 'var(--acento)' : 'var(--error)') }}>{ok ? '✓' : '!'}</div>
              : <div style={{ fontSize: '4rem', marginBottom: '0.25rem' }}>{ok ? '🎉' : '💪'}</div>}
            <Score $ok={ok} style={temaS ? { color: ok ? '#3ddc84' : 'var(--acento)' } : temaClaro ? { color: ok ? 'var(--acento)' : 'var(--error)' } : undefined}>{Math.round(resultado.porcentaje)}<span style={{ fontSize: '0.4em' }}>%</span></Score>
            <ResultBadge $ok={ok} style={temaS ? { color: ok ? '#3ddc84' : '#fff', background: ok ? 'rgba(61,220,132,.12)' : 'var(--acento)', borderColor: 'transparent' } : temaClaro ? { color: ok ? 'var(--acento)' : 'var(--error)', background: ok ? '#fff' : '#fdecea', borderColor: ok ? 'var(--acento)' : 'var(--error)' } : undefined}>
              {sobrio ? (ok ? 'Aprobado' : 'No aprobado') : (ok ? '✓ APROBADO' : '× NO APROBADO')}
            </ResultBadge>
            <p style={{ margin: '1.25rem 0 0', opacity: 0.7, fontSize: '0.92rem' }}>
              Necesitabas <strong>{Math.round(resultado.porcentaje_aprobacion || porcAprob)}%</strong>
              {' · '}{respuestasRef.current.length} preguntas
            </p>
            {resultado.certificado?.emitido && (
              <CertBlock>
                <span style={{ fontSize: '1.5rem' }}>🏆</span>
                <div style={{ textAlign: 'left' }}>
                  <div style={{ fontWeight: 700 }}>Certificado emitido</div>
                  <div style={{ fontSize: '0.82rem', opacity: 0.85 }}>código <strong>{resultado.certificado.codigo}</strong></div>
                </div>
              </CertBlock>
            )}
            <div style={{ marginTop: '1.75rem', display: 'flex', gap: '0.6rem', justifyContent: 'center', flexWrap: 'wrap' }}>
              {examenId
                ? <BtnP $primary onClick={() => navigate(`/mentor/${documentId}`)}>← Volver al curso</BtnP>
                : <>
                    <Btn onClick={() => navigate('/contenidos')}>← {t('ir_contenidos')}</Btn>
                    <BtnP $primary onClick={() => navigate('/perfil')}>Mi perfil</BtnP>
                  </>}
            </div>
          </ResultCard>

          {resultado.resumen_global && (
            <div style={temaS
              ? { marginTop: '1rem', background: '#1d1d1d', borderLeft: '3px solid var(--acento)', borderRadius: 8, padding: '1rem 1.2rem', textAlign: 'left' }
              : temaClaro
              ? { marginTop: '1rem', background: 'var(--tarjeta)', border: '1px solid #d3ebe8', borderLeft: '4px solid var(--acento)', borderRadius: 14, padding: '1rem 1.2rem', textAlign: 'left' }
              : { marginTop: '1rem', background: 'linear-gradient(135deg, rgba(34,211,238,0.10), rgba(6,182,212,0.05))', border: '1px solid rgba(34,211,238,0.3)', borderRadius: 14, padding: '1rem 1.2rem', textAlign: 'left' }}>
              <div style={{ fontSize: '0.72rem', fontWeight: 800, letter: 1.2, color: temaS ? '#9aa1ab' : temaClaro ? 'var(--acento)' : '#67e8f9', textTransform: 'uppercase', marginBottom: 6 }}>
                {sobrio ? '' : '💬 '}{idioma === 'en' ? 'Feedback' : idioma === 'pt' ? 'Retroalimentação' : 'Retroalimentación'}
              </div>
              <div style={{ fontSize: '0.95rem', lineHeight: 1.55, color: temaClaro ? 'var(--texto)' : '#e2e8f0' }}>{resultado.resumen_global}</div>
            </div>
          )}

          <details open style={{ marginTop: '1rem', background: 'rgba(255,255,255,0.04)', borderRadius: 12, padding: '0.75rem 1rem' }}>
            <summary style={{ cursor: 'pointer', opacity: 0.75 }}>Detalle por pregunta ({respuestasRef.current.length})</summary>
            <div style={{ marginTop: '0.75rem', display: 'flex', flexDirection: 'column', gap: '0.6rem' }}>
              {respuestasRef.current.map((r, i) => (
                <div key={i} style={{ padding: '0.75rem', background: temaClaro ? '#fff' : temaS ? '#141414' : 'rgba(0,0,0,0.25)', borderRadius: 10, borderLeft: `3px solid ${colorNivel(r.nivel)}` }}>
                  <div style={{ fontSize: '0.8rem', opacity: temaClaro ? 1 : 0.7, color: temaClaro ? colorNivel(r.nivel) : undefined, fontWeight: temaClaro ? 800 : undefined }}>
                    Pregunta {r.numero_pregunta} · {sobrio ? '' : `${NIVEL_EMOJI[r.nivel]} `}{r.nivel}
                  </div>
                  <div style={{ marginTop: 4, fontWeight: 500 }}>{r.pregunta_texto}</div>
                  <div style={{ marginTop: 6, fontSize: '0.85rem', opacity: 0.85 }}>
                    <em>Tu respuesta:</em> {r.respuesta_usuario}
                  </div>
                  <div style={{ marginTop: 4, fontSize: '0.85rem', color: temaClaro ? '#5b6a6b' : (NIVEL_COLOR[r.nivel] || '#94a3b8') }}>{r.feedback}</div>
                </div>
              ))}
            </div>
          </details>
        </Container>
      </Pg>
    );
  }

  // ═════ EN CURSO ═════
  const s = statusInfo();
  const chips = quickChips();

  return (
    <Pg {...temaProps}>
      <Container>
        <TopBar>
          <DocTitle>
            {!sobrio && <span>📝</span>}
            <span style={{ whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', maxWidth: '200px' }}>
              {meta?.document_title || 'Evaluación'}
            </span>
            <IdiomaPill>{(idioma || 'es').toUpperCase()}</IdiomaPill>
          </DocTitle>
          <Timer>⏱ {fmtMMSS(tickMs)}</Timer>
        </TopBar>

        <Dots>
          {Array.from({ length: totalPreguntas || 10 }).map((_, i) => (
            <Dot key={i} $done={i < progress} $current={i === progress} $claro={!!temaClaro} $s={!!temaS} />
          ))}
        </Dots>

        {micUnavailable && (
          <div style={{
            background: 'linear-gradient(135deg, rgba(251,191,36,0.15), rgba(245,158,11,0.10))',
            border: '1px solid rgba(251,191,36,0.4)',
            borderRadius: 12,
            padding: '0.7rem 1rem',
            color: '#FBBF24',
            fontSize: '0.88rem',
            display: 'flex',
            alignItems: 'center',
            gap: 8,
          }}>
            <span style={{ fontSize: '1.2rem' }}>🎙️🚫</span>
            <span>
              {idioma === 'en' ? 'Microphone unavailable — type your answers below.' :
               idioma === 'pt' ? 'Microfone indisponível — digite suas respostas abaixo.' :
               'Sin micrófono — escribí tus respuestas abajo.'}
            </span>
          </div>
        )}

        {noVoiceDetected && !micUnavailable && (
          <div style={{
            background: 'linear-gradient(135deg, rgba(239,68,68,0.18), rgba(220,38,38,0.10))',
            border: '1px solid rgba(239,68,68,0.45)',
            borderRadius: 12,
            padding: '0.85rem 1rem',
            color: '#fca5a5',
            fontSize: '0.9rem',
            display: 'flex',
            alignItems: 'center',
            gap: 10,
          }}>
            <span style={{ fontSize: '1.4rem' }}>⚠️</span>
            <div>
              <div style={{ fontWeight: 700, marginBottom: 2, color: '#fecaca' }}>
                {idioma === 'en' ? 'No answer detected' : idioma === 'pt' ? 'Resposta não detectada' : 'No se reconoció tu respuesta'}
              </div>
              <div style={{ fontSize: '0.82rem', opacity: 0.9 }}>
                {idioma === 'en' ? 'Try speaking louder, closer to the mic, or type your answer below.' :
                 idioma === 'pt' ? 'Tente falar mais alto, mais perto do microfone, ou digite sua resposta abaixo.' :
                 'Hablá más fuerte y más cerca del micrófono, o escribí tu respuesta abajo.'}
              </div>
            </div>
          </div>
        )}

        <QuestionCard>
          <WatermarkNum>{String(Math.min(progress + 1, totalPreguntas || progress + 1)).padStart(2, '0')}</WatermarkNum>
          {tipoCurrent && <TipoChip>{TIPO_LABEL[tipoCurrent] || tipoCurrent}</TipoChip>}
          <PreguntaNum>
            {idioma === 'en' ? 'Question' : idioma === 'pt' ? 'Pergunta' : 'Pregunta'}
            {' '}{Math.min(progress + 1, totalPreguntas || progress + 1)}
            {' '}{idioma === 'en' ? 'of' : 'de'}
            {' '}{totalPreguntas || '?'}
          </PreguntaNum>
          <Pregunta>
            {currentQuestion || (
              <span style={{ opacity: 0.5 }}>
                {stage === 'conectando' ? 'Conectando con el evaluador…' :
                 stage === 'finalizando' ? 'Calificando…' : 'El evaluador está preparando tu primera pregunta…'}
              </span>
            )}
            {streamingAi && <Cursor />}
          </Pregunta>

          <Waves $active={aiState === SESSION_STATES.AI_SPEAKING || aiState === SESSION_STATES.LISTENING}>
            {Array.from({ length: 9 }).map((_, i) => (
              <Bar key={i} $i={i} $active={aiState === SESSION_STATES.AI_SPEAKING || aiState === SESSION_STATES.LISTENING} />
            ))}
          </Waves>
        </QuestionCard>

        <InputCard>
          <Textarea
            rows={2}
            placeholder="Escribe aquí tu respuesta (o usa el micrófono). Enter para enviar"
            value={inputText}
            onChange={e => setInputText(e.target.value)}
            onKeyDown={e => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); sendText(); } }}
            disabled={stage !== 'en_curso'}
          />
          <ActionsRow>
            <MicBtn
              $listening={aiState === SESSION_STATES.LISTENING}
              $speaking={aiState === SESSION_STATES.AI_SPEAKING}
              disabled={stage !== 'en_curso'}
              onClick={() => clientRef.current?.interrupt()}
              title="Toca para interrumpir al evaluador (sigues hablando por voz siempre)"
            >🎤</MicBtn>
            <StatusLabel><StatusDot $color={s.dot} />{s.txt}</StatusLabel>
            <SendBtn onClick={sendText} disabled={stage !== 'en_curso' || !inputText.trim()}>Enviar</SendBtn>
          </ActionsRow>
          {chips && stage === 'en_curso' && (
            <QuickChips>
              {chips.map(c => <QuickChip key={c} onClick={() => sendQuick(c)}>{c}</QuickChip>)}
            </QuickChips>
          )}
        </InputCard>

        <Hint>
          El audio se transmite en vivo · puedes hablar o escribir en cualquier momento
        </Hint>
      </Container>

      {feedbackToast && (
        <FeedbackToast $color={feedbackToast.color}>
          <FeedbackBadge $color={feedbackToast.color}>
            {sobrio ? '' : `${NIVEL_EMOJI[feedbackToast.nivel]} `}{feedbackToast.nivel} · {feedbackToast.puntaje} pts
          </FeedbackBadge>
          <div style={{ fontSize: '0.92rem' }}>{feedbackToast.feedback}</div>
        </FeedbackToast>
      )}
    </Pg>
  );
};

// Pantalla de elección: al entrar a /evaluacion/:id el alumno decide cómo responder.
const ModoChooser = ({ onPick, onPruebas, pruebasMsg }) => {
  // Diseño claro del proyecto (ficha → evaluacion_tema): mismas piezas que el examen escrito.
  const { proyecto } = useProject();
  const temaS = temaStreaming(proyecto);
  const tema = temaS ? null : temaEvaluacion(proyecto);
  const { documentId } = useParams();
  const [sp] = useSearchParams();
  const examenId = parseInt(sp.get('examen') || '', 10) || null;
  const navigate = useNavigate();
  const [info, setInfo] = useState(null);
  useEffect(() => {
    if (!temaS) return;
    evaluacionEscritaService.info(parseInt(documentId, 10), examenId).then(r => { if (r.data?.success) setInfo(r.data); }).catch(() => {});
  }, [!!temaS, documentId, examenId]); // eslint-disable-line react-hooks/exhaustive-deps
  if (temaS) {
    return (
      <RaizS {...temaS}>
        <EntradaS>
          <MuroCursos proyecto={proyecto} fondo={temaS.$fondo || '#141414'} acento={temaS.$acento || '#E50914'} opacidad={0.6} />
          <span className="eyebrow">Evaluación</span>
          <h1>{info?.titulo || (info?.curso ? `Evaluación de ${info.curso}` : 'Evaluación')}</h1>
          <p className="bajada">
            {info?.curso && <>Del curso <b>{info.curso}</b>. </>}
            {info ? `${info.total_preguntas} preguntas; apruebas con ${Math.round(info.porcentaje_aprobacion)}\u00a0%. ` : ''}Elige cómo quieres responder.
          </p>
          <OpcionesModo>
            <button type="button" onClick={() => onPick('oral')}>
              <i><IcoMic /></i><b>Oral</b><span>El evaluador te habla y respondes por micrófono</span>
            </button>
            <button type="button" onClick={() => onPick('escrita')}>
              <i><IcoLapiz /></i><b>Escrita</b><span>Lees la pregunta y respondes escribiendo o eligiendo una opción</span>
            </button>
          </OpcionesModo>
          <div className="botones" style={{ marginTop: '1.2rem' }}>
            <PiezasStreaming.Btn onClick={() => navigate(examenId ? `/mentor/${documentId}` : '/contenidos')}>Volver al curso</PiezasStreaming.Btn>
            {onPruebas && <PiezasStreaming.Btn onClick={onPruebas}>Aprobar sin responder (modo pruebas)</PiezasStreaming.Btn>}
          </div>
          {pruebasMsg && <p className="aviso" style={{ color: '#c3c9d1' }}>{pruebasMsg}</p>}
        </EntradaS>
      </RaizS>
    );
  }
  const Pg = tema ? TemaClaro.Page : Page;
  const Ct = tema ? TemaClaro.Container : Container;
  const Cd = tema ? TemaClaro.Card : QuestionCard;
  const Bt = tema ? TemaClaro.Btn : Btn;
  return (
  <Pg {...(tema || {})}>
    <Ct>
      <Cd style={{ textAlign: 'center', paddingTop: '3rem', paddingBottom: '2.5rem' }}>
        {!tema && <div style={{ fontSize: '3.5rem', marginBottom: '.5rem' }}>📝</div>}
        <h1 style={{ margin: '0 0 .5rem', fontSize: 'clamp(1.5rem,4vw,2rem)', ...(tema ? { color: 'var(--acento)', fontWeight: 800, textTransform: 'uppercase' } : {}) }}>Evaluación</h1>
        <p style={{ color: tema ? '#6b7a7b' : 'rgba(255,255,255,0.7)', margin: '0 auto 2rem', maxWidth: 460, lineHeight: 1.6 }}>
          ¿Cómo querés responder?
        </p>
        <div style={{ display: 'flex', gap: '1rem', justifyContent: 'center', flexWrap: 'wrap' }}>
          <Bt $primary onClick={() => onPick('oral')} style={{ padding: '1.1rem 1.6rem', fontSize: '1rem', minWidth: 220, textTransform: 'none' }}>
            {tema ? 'Oral' : '🎙️ Oral'}<br /><span style={{ fontWeight: 400, fontSize: '.8rem', opacity: .85 }}>El evaluador te habla y respondés por micrófono</span>
          </Bt>
          <Bt onClick={() => onPick('escrita')} style={{ padding: '1.1rem 1.6rem', fontSize: '1rem', minWidth: 220, textTransform: 'none' }}>
            {tema ? 'Escrita' : '✍️ Escrita'}<br /><span style={{ fontWeight: 400, fontSize: '.8rem', opacity: .85 }}>Lees la pregunta y respondés escribiendo</span>
          </Bt>
        </div>
        {onPruebas && (
          <div style={{ marginTop: '1.6rem' }}>
            <Btn
              onClick={onPruebas}
              // En el diseño claro, verde oscuro (el amarillo no se leía sobre blanco).
              style={tema
                ? { padding: '0.6rem 1.2rem', fontSize: '.82rem', fontWeight: 700, background: '#167e75', border: '1px solid #167e75', color: '#fff' }
                : { padding: '0.6rem 1.2rem', fontSize: '.82rem', border: '1px solid rgba(251,191,36,.5)', color: '#fde68a' }}
            >
              ✓ Aprobar sin responder (modo pruebas)
            </Btn>
            {pruebasMsg && (
              <div style={{ marginTop: '.6rem', fontSize: '.78rem', color: tema ? '#6b7a7b' : 'rgba(255,255,255,0.72)' }}>{pruebasMsg}</div>
            )}
          </div>
        )}
      </Cd>
    </Ct>
  </Pg>
  );
};

// La misma ruta /evaluacion/:id sirve: elección → oral (Realtime) o escrito (lectura TTS + tecleo).
const EvaluacionRealtimePageSafe = (props) => {
  const [searchParams, setSearchParams] = useSearchParams();
  const { documentId } = useParams();
  const { user } = useAuth();
  const navigate = useNavigate();
  // Modo pruebas (QA): el tester puede dar la evaluación por aprobada para
  // revisar certificados y desbloqueos sin responderla.
  const [pruebasMsg, setPruebasMsg] = useState('');
  const aprobarEnPruebas = async () => {
    try {
      setPruebasMsg('Marcando…');
      const res = await mentor2Service.completarPrueba({ documentId, alcance: 'evaluacion' });
      setPruebasMsg(res.data?.message || 'Evaluación aprobada');
      setTimeout(() => navigate('/contenidos'), 1200);
    } catch (err) {
      setPruebasMsg(err.response?.data?.message || 'No se pudo marcar');
    }
  };
  const modo = searchParams.get('modo');
  if (modo === 'escrita') return <EvaluacionEscritaPage />;
  if (modo === 'oral') {
    return (
      <EvalErrorBoundary>
        <EvaluacionRealtimePage {...props} />
      </EvalErrorBoundary>
    );
  }
  // Sin modo → pantalla de elección
  return (
    <ModoChooser
      onPick={(m) => { const p = new URLSearchParams(searchParams); p.set('modo', m); setSearchParams(p); }}
      onPruebas={user?.modo_pruebas ? aprobarEnPruebas : null}
      pruebasMsg={pruebasMsg}
    />
  );
};

export default EvaluacionRealtimePageSafe;
